import { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { NotificationResponse } from '@/api/data-contracts';
import { SEVERITY_ACCENT_COLORS } from '@/features/notifications/ui/Calendar/shared/Calendar.constants';
import {
  BellIcon,
  ClockIcon,
  ExclamationIcon,
} from '@/features/notifications/ui/Calendar/shared/Icons';
import { useOrdersService } from '@/features/orders';
import { queryKeys } from '@/react-query/constants';
import { tryParseJson } from '@/shared/lib/json';

import useNotificationsService from '../../api/notifications.service';
import useNotificationsStore from '../../model/notifications';
import { notificationBodyContext } from '../../model/weTransferReason';

type Severity = 'all' | 'critical' | 'important' | 'common';
type TimeFilter = 'all' | 'today' | 'yesterday' | 'last_7_days' | 'last_30_days' | 'this_month';

const DATE_GROUPS = ['today', 'yesterday', 'this_week', 'this_month', 'older'] as const;
type DateGroup = (typeof DATE_GROUPS)[number];

export const getDateGroup = (timestamp: string): DateGroup => {
  const date = dayjs(timestamp);
  const now = dayjs();
  if (date.isSame(now, 'day')) return 'today';
  if (date.isSame(now.subtract(1, 'day'), 'day')) return 'yesterday';
  if (date.isSame(now, 'week')) return 'this_week';
  if (date.isSame(now, 'month')) return 'this_month';
  return 'older';
};

interface ChipConfig {
  key: Severity;
  label: string;
  color: string | null;
  icon?: ReactElement;
}

interface NotificationsSideMenuFilters {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  activeSeverity: Severity;
  setActiveSeverity: (value: Severity) => void;
  timeFilter: TimeFilter;
  setTimeFilter: (value: TimeFilter) => void;
  timeAnchorEl: HTMLElement | null;
  setTimeAnchorEl: (value: HTMLElement | null) => void;
  timeOptions: { key: TimeFilter; label: string }[];
  chips: ChipConfig[];
  groupLabels: Record<DateGroup, string>;
  filteredNotifications: NotificationResponse[];
  counts: { all: number; critical: number; important: number; common: number };
  pendingOrdersCount: number;
  dateGroups: typeof DATE_GROUPS;
}

export default function useNotificationsSideMenuFilters(
  notifications: NotificationResponse[],
  hasOrdersAccess: boolean,
  externalSearchQuery?: string,
  externalSetSearchQuery?: (value: string) => void,
  targetModule?: string,
): NotificationsSideMenuFilters {
  const { t } = useTranslation();
  const { getOrders } = useOrdersService();
  const { getNotificationsCountBreakdown } = useNotificationsService();
  const showReadNotifications = useNotificationsStore((s) => s.showReadNotifications);

  const [internalSearchQuery, internalSetSearchQuery] = useState('');
  const searchQuery = externalSearchQuery ?? internalSearchQuery;
  const setSearchQuery = externalSetSearchQuery ?? internalSetSearchQuery;
  const [activeSeverity, setActiveSeverity] = useState<Severity>('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [timeAnchorEl, setTimeAnchorEl] = useState<HTMLElement | null>(null);

  const { data: pendingOrdersCount = 0 } = useQuery({
    queryKey: [queryKeys.orders, 'pending-count'],
    queryFn: async ({ signal }) => {
      const response = await getOrders(
        { request: { pageNumber: 0, numberOfItems: 1, status: ['PENDING'] } },
        signal,
      );
      return response.totalElements ?? 0;
    },
    enabled: hasOrdersAccess,
  });

  const { data: backendBreakdown } = useQuery({
    queryKey: [queryKeys.notificationsCountBreakdown, targetModule ?? 'all-modules'],
    queryFn: ({ signal }) => getNotificationsCountBreakdown(signal, targetModule),
  });

  const timeOptions: { key: TimeFilter; label: string }[] = [
    { key: 'today', label: t('today', { defaultValue: 'Today' }) },
    { key: 'yesterday', label: t('yesterday', { defaultValue: 'Yesterday' }) },
    { key: 'last_7_days', label: t('last_7_days', { defaultValue: 'Last 7 days' }) },
    { key: 'last_30_days', label: t('last_30_days', { defaultValue: 'Last 30 days' }) },
    { key: 'this_month', label: t('this_month', { defaultValue: 'This month' }) },
    { key: 'all', label: t('all', { defaultValue: 'All' }) },
  ];

  const timeFilteredNotifications = useMemo(() => {
    if (timeFilter === 'all') return notifications;
    const now = dayjs();
    return notifications.filter((n) => {
      const date = dayjs(n.audit.timestamp);
      if (timeFilter === 'today') return date.isSame(now, 'day');
      if (timeFilter === 'yesterday') return date.isSame(now.subtract(1, 'day'), 'day');
      if (timeFilter === 'last_7_days') return !date.isBefore(now.subtract(7, 'day'));
      if (timeFilter === 'last_30_days') return !date.isBefore(now.subtract(30, 'day'));
      if (timeFilter === 'this_month') return date.isSame(now, 'month');
      return true;
    });
  }, [notifications, timeFilter]);

  const searchFilteredNotifications = useMemo(() => {
    if (!searchQuery.trim()) return timeFilteredNotifications;
    const query = searchQuery.toLowerCase();
    return timeFilteredNotifications.filter((n) => {
      const title = t(`notifications.title.${n.audit.type}`, {
        orderNumber: '-',
        pendingOrdersCount,
      });
      const parsed = tryParseJson(n.audit.details || '{}');
      const body = t(`notifications.body.${n.audit.type}`, {
        ...parsed,
        defaultValue: '',
        context: notificationBodyContext(parsed),
      }).replace(/<[^>]*>/g, '');
      return title.toLowerCase().includes(query) || body.toLowerCase().includes(query);
    });
  }, [timeFilteredNotifications, searchQuery, t, pendingOrdersCount]);

  const filteredNotifications = useMemo(() => {
    const severityFiltered =
      activeSeverity === 'all'
        ? searchFilteredNotifications
        : searchFilteredNotifications.filter(
            (n) => (n.severity ?? 'COMMON') === activeSeverity.toUpperCase(),
          );

    if (!searchQuery.trim() || !showReadNotifications) return severityFiltered;

    return [...severityFiltered].sort((a, b) => {
      if (a.status === b.status) return 0;
      return a.status === 'UNREAD' ? -1 : 1;
    });
  }, [searchFilteredNotifications, activeSeverity, searchQuery, showReadNotifications]);

  const counts = useMemo(() => {
    const hasActiveFilters = timeFilter !== 'all' || searchQuery.trim() !== '';
    if (!hasActiveFilters && !showReadNotifications && backendBreakdown) {
      const total =
        backendBreakdown.critical + backendBreakdown.important + backendBreakdown.common;
      return {
        all: total,
        critical: backendBreakdown.critical,
        important: backendBreakdown.important,
        common: backendBreakdown.common,
      };
    }
    return {
      all: searchFilteredNotifications.length,
      critical: searchFilteredNotifications.filter((n) => (n.severity ?? 'COMMON') === 'CRITICAL')
        .length,
      important: searchFilteredNotifications.filter((n) => (n.severity ?? 'COMMON') === 'IMPORTANT')
        .length,
      common: searchFilteredNotifications.filter((n) => (n.severity ?? 'COMMON') === 'COMMON')
        .length,
    };
  }, [
    timeFilter,
    searchQuery,
    showReadNotifications,
    backendBreakdown,
    searchFilteredNotifications,
  ]);

  const groupLabels: Record<DateGroup, string> = {
    today: t('today', { defaultValue: 'Today' }),
    yesterday: t('yesterday', { defaultValue: 'Yesterday' }),
    this_week: t('this_week', { defaultValue: 'This week' }),
    this_month: t('this_month', { defaultValue: 'This month' }),
    older: t('older', { defaultValue: 'Older' }),
  };

  const chips: ChipConfig[] = [
    { key: 'all', label: `${t('all', { defaultValue: 'All' })} ${counts.all}`, color: null },
    {
      key: 'critical',
      label: t('notifications.severity.critical', { defaultValue: 'Critical' }),
      color: SEVERITY_ACCENT_COLORS.CRITICAL,
      icon: <ExclamationIcon color="currentColor" width={16} height={16} />,
    },
    {
      key: 'important',
      label: t('notifications.severity.important', { defaultValue: 'Important' }),
      color: SEVERITY_ACCENT_COLORS.IMPORTANT,
      icon: <BellIcon color="currentColor" width={16} height={16} />,
    },
    {
      key: 'common',
      label: t('notifications.severity.common', { defaultValue: 'Common' }),
      color: SEVERITY_ACCENT_COLORS.COMMON,
      icon: <ClockIcon color="currentColor" width={16} height={16} />,
    },
  ];

  return {
    searchQuery,
    setSearchQuery,
    activeSeverity,
    setActiveSeverity,
    timeFilter,
    setTimeFilter,
    timeAnchorEl,
    setTimeAnchorEl,
    timeOptions,
    chips,
    groupLabels,
    filteredNotifications,
    counts,
    pendingOrdersCount,
    dateGroups: DATE_GROUPS,
  };
}
